import type { Id } from '@vp/shared-types';
import { applyArchive, ArchiveError, buildArchive, FormatTooNewError, readArchive } from '@vp/core';
import { logger, storage } from './platform';
import { download, safeFileName } from './export';
import { t } from './i18n';
import { installedModuleIds, moduleVersions } from './modules';
import type { useApp } from './stores/app';

type App = ReturnType<typeof useApp>;

async function markExported(ids: Id[]): Promise<void> {
  const now = Date.now();
  await storage.setSetting('lastExportAt', now);
  for (const id of ids) await storage.updateCanvas(id, { lastExportAt: now });
}

export async function exportCanvasArchive(id: Id): Promise<void> {
  await exportCanvases([id]);
}

/** One `.vpz` of the given canvases. A single canvas keeps its name; several share a dated name. */
export async function exportCanvases(ids: readonly Id[]): Promise<void> {
  if (!ids.length) return;
  const metas = await Promise.all(ids.map((id) => storage.getCanvas(id)));
  const found = ids.filter((_, i) => metas[i]);
  if (!found.length) return;
  const data = await buildArchive(storage, found, { app: __APP_VERSION__, moduleVersions: moduleVersions() });
  const date = new Date().toISOString().slice(0, 10);
  const only = found.length === 1 ? metas[ids.indexOf(found[0])] : undefined;
  const name = safeFileName(only?.name ?? `canvases-${date}`);
  download(new Blob([data as BlobPart], { type: 'application/zip' }), `${name}.vpz`);
  await markExported(found);
  logger.info('export.archive', { canvases: found.length, bytes: data.byteLength });
}

/**
 * Save the open canvases to a file from what is on screen: when saving to storage fails the stored
 * copy is behind, and this is the way to keep the work.
 */
export async function exportOpenCanvasesNow(app: App): Promise<void> {
  const open = [...app.sessions.values()];
  if (!open.length) return;
  const ids = open.map((s) => s.id);
  const live = new Map(open.map((s) => [s.id, { objects: [...s.doc.allObjects()], links: [...s.doc.allLinks()] }]));
  // the canvas record may be missing from storage if it could never be written
  for (const s of open) if (!(await storage.getCanvas(s.id).catch(() => undefined))) await storage.putCanvas({ ...s.meta.value }).catch(() => undefined);
  const data = await buildArchive(storage, ids, { app: __APP_VERSION__, moduleVersions: moduleVersions(), live: (id) => live.get(id) });
  const date = new Date().toISOString().slice(0, 10);
  const name = safeFileName(open.length === 1 ? open[0].meta.value.name : 'canvases-' + date);
  download(new Blob([data as BlobPart], { type: 'application/zip' }), name + '.vpz');
  await markExported(ids).catch(() => undefined);
  logger.info('export.archive', { canvases: ids.length, bytes: data.byteLength, live: true });
}

export async function exportAllCanvases(): Promise<void> {
  const ids = (await storage.listCanvases()).map((c) => c.id);
  const data = await buildArchive(storage, ids, { app: __APP_VERSION__, moduleVersions: moduleVersions() });
  const date = new Date().toISOString().slice(0, 10);
  download(new Blob([data as BlobPart], { type: 'application/zip' }), `canvases-${date}.vpz`);
  await markExported(ids);
  logger.info('export.archive', { canvases: ids.length, bytes: data.byteLength });
}

export async function importArchiveFile(file: File, app: App): Promise<void> {
  try {
    const parsed = await readArchive(new Uint8Array(await file.arrayBuffer()));
    const created = await applyArchive(storage, parsed);
    await app.refreshCanvases();
    app.notify('info', t('import.done', { n: created.length }));
    const installed = new Set(installedModuleIds());
    const missing = parsed.modules.filter((m) => !installed.has(m));
    if (missing.length) app.notify('warning', t('import.missingModules', { list: missing.join(', ') }), 10000);
    logger.info('import.archive', { canvases: created.length, missing: missing.length });
  } catch (e) {
    if (e instanceof FormatTooNewError) app.notify('error', t('import.tooNew', { v: e.found }));
    else app.notify('error', t('import.invalid', { reason: e instanceof ArchiveError ? e.message : e instanceof Error ? e.message : String(e) }));
    logger.warn('import.archive.failed', { error: e instanceof Error ? e.name : 'unknown' });
  }
}
